// Shared building blocks for the isolated "Asset Intelligence V2 (Sample)" pages.
// These pages read ONLY from static workbook data — no live site/API/DB data.
import { useMemo, useState } from "react";
import { MagnifyingGlass } from "@phosphor-icons/react";

export const NAVY = "#0A0A0A";
export const BLUE = "#0055FF";
export const GROUP_COLORS = ["#0055FF", "#0A0A0A", "#7C3AED", "#059669", "#D97706", "#DC2626", "#0891B2", "#DB2777", "#65A30D", "#9333EA"];

export const fmt$ = (n) => "$" + Math.round(n || 0).toLocaleString();
export const fmtK = (n) => "$" + Math.round((n || 0) / 1000).toLocaleString() + "k";
export const fmtNum = (n) => (n == null ? "—" : Number(n).toLocaleString());

const MONEY_HINT = /budget|cost|value|material|labour|replacement|forecast|spend|component/i;
const NUM_HINT = /count|assets|score|condition|risk|years|age|rank|confidence|util|environ|health|life|year$/i;

export function Kpi({ icon: Icon, label, value, sub, accent = "text-[#0A0A0A]" }) {
  return (
    <div className="rounded-sm border border-neutral-200 bg-white p-4 shadow-sm" data-testid="aiv2-kpi">
      <div className="mb-2 flex items-center gap-2 text-neutral-400">
        {Icon && <Icon size={16} />}
        <span className="text-xs font-semibold uppercase tracking-wider">{label}</span>
      </div>
      <p className={`font-heading text-2xl font-black tracking-tight ${accent}`}>{value}</p>
      {sub && <p className="mt-0.5 text-xs text-neutral-500">{sub}</p>}
    </div>
  );
}

export function Panel({ title, subtitle, right, children, testid }) {
  return (
    <div className="rounded-sm border border-neutral-200 bg-white p-5 shadow-sm" data-testid={testid}>
      {(title || right) && (
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            {title && <h3 className="font-heading text-lg font-black tracking-tight text-[#0A0A0A]">{title}</h3>}
            {subtitle && <p className="text-xs text-neutral-500">{subtitle}</p>}
          </div>
          {right}
        </div>
      )}
      {children}
    </div>
  );
}

export function TabBar({ tabs, active, onChange, testidPrefix = "aiv2-tab" }) {
  return (
    <div className="mb-5 flex flex-wrap gap-1 border-b border-neutral-200">
      {tabs.map((t) => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          data-testid={`${testidPrefix}-${t.id}`}
          className={`flex items-center gap-2 border-b-2 px-3.5 py-2.5 text-sm font-semibold transition-colors ${active === t.id ? "border-[#0055FF] text-[#0055FF]" : "border-transparent text-neutral-500 hover:text-[#0A0A0A]"}`}
        >
          {t.icon && <t.icon size={15} weight="bold" />} {t.label}
        </button>
      ))}
    </div>
  );
}

// Renders an array-of-objects as a scrollable, searchable table. Money/number columns auto-aligned.
export function AutoTable({ rows, columns, search = true, maxHeight = "30rem", testid = "aiv2-table" }) {
  const [q, setQ] = useState("");
  const cols = useMemo(() => columns || (rows[0] ? Object.keys(rows[0]) : []), [columns, rows]);
  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const t = q.toLowerCase();
    return rows.filter((r) => cols.some((c) => String(r[c] ?? "").toLowerCase().includes(t)));
  }, [q, rows, cols]);

  const cell = (col, v) => {
    if (v == null || v === "") return "—";
    if (typeof v === "number" && MONEY_HINT.test(col)) return fmt$(v);
    if (typeof v === "number") return fmtNum(v);
    return String(v);
  };
  const alignRight = (col) => MONEY_HINT.test(col) || NUM_HINT.test(col);

  return (
    <div data-testid={testid}>
      {search && (
        <div className="relative mb-3 max-w-xs">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search rows…"
            data-testid={`${testid}-search`}
            className="w-full rounded-sm border border-neutral-300 py-1.5 pl-9 pr-3 text-sm outline-none focus:border-[#0055FF]"
          />
        </div>
      )}
      <div className="overflow-auto rounded-sm border border-neutral-200" style={{ maxHeight }}>
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 border-b border-neutral-200 bg-neutral-50 text-left text-[11px] uppercase tracking-wider text-neutral-500">
            <tr>{cols.map((c) => <th key={c} className={`whitespace-nowrap px-3 py-2.5 font-semibold ${alignRight(c) ? "text-right" : ""}`}>{c}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {filtered.map((r, i) => (
              <tr key={i} className="hover:bg-neutral-50" data-testid={`${testid}-row`}>
                {cols.map((c) => (
                  <td key={c} className={`px-3 py-2 ${alignRight(c) ? "text-right font-mono text-neutral-700" : "text-neutral-700"}`}>{cell(c, r[c])}</td>
                ))}
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={cols.length} className="py-8 text-center text-neutral-400">No matching rows.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-xs text-neutral-400">{filtered.length.toLocaleString()} of {rows.length.toLocaleString()} rows</p>
    </div>
  );
}

// Compact condition/risk severity pill (1 good .. 5 bad)
export function SeverityBadge({ value }) {
  const tones = { 1: "bg-[#059669]/10 text-[#059669]", 2: "bg-[#65A30D]/10 text-[#65A30D]", 3: "bg-[#D97706]/10 text-[#D97706]", 4: "bg-[#EA580C]/10 text-[#EA580C]", 5: "bg-[#DC2626]/10 text-[#DC2626]" };
  return <span className={`inline-block rounded-sm px-1.5 py-0.5 text-xs font-bold ${tones[value] || "bg-neutral-100 text-neutral-500"}`}>{value ?? "—"}</span>;
}
